import { useLayoutEffect, useState } from "react";

const INITIAL_WIDTHS = [190, 56, 120, 120, 180, 104, 72, 60, 116, 44, 0];
const IDENTITY_MIN_WIDTHS = [190, 56, 104, 104];
const HEADER_PADDING = 8;
const BODY_PADDING = 8;
const NUMERIC_PADDING = 10;
const EDGE_INSET = 14;
const INITIAL_GEOMETRY = {
  widths: INITIAL_WIDTHS,
  tableWidth: INITIAL_WIDTHS.reduce((sum, width) => sum + width, 0),
};

function textWidth(element) {
  if (!element) return 0;
  const range = document.createRange();
  range.selectNodeContents(element);
  return range.getBoundingClientRect?.().width ?? 0;
}

function headerContentWidth(header, edge = false) {
  const title = header.querySelector(`[data-testid^="leases-list-header-group-"] > span`);
  if (!title) return 0;
  // The caret sits outside the centred label, so reserve its width on BOTH
  // sides, even at the silent default sort or while the pointer is elsewhere.
  const range = document.createRange();
  range.selectNodeContents(title);
  if (title.firstChild?.nodeType === Node.TEXT_NODE) range.selectNode(title.firstChild);
  const caret = header.querySelector("svg");
  const caretWidth = caret ? caret.getBoundingClientRect().width + 1 : 0;
  const labelWidth = range.getBoundingClientRect?.().width ?? 0;
  // The absolute caret may occupy the header's padding, as in Offices. Reserve
  // the larger of symmetric label insets or caret clearance, not their sum.
  return labelWidth + Math.max(caretWidth * 2 + HEADER_PADDING, edge ? EDGE_INSET * 2 : HEADER_PADDING);
}

function trailingContentWidths(row) {
  const cells = row.cells;
  const date = cells[4].querySelector('[data-testid="expire-date-value"]');
  const countdown = cells[4].querySelector('[data-testid="expire-countdown"]');
  const separator = countdown?.previousElementSibling;
  const separatorStyle = separator ? getComputedStyle(separator) : null;
  const expiry = textWidth(date) + textWidth(countdown) + textWidth(separator)
    + (separatorStyle ? parseFloat(separatorStyle.marginLeft) + parseFloat(separatorStyle.marginRight) : 0);
  const arrangement = Math.max(
    textWidth(cells[5].querySelector('[data-testid="lease-type-value"]')),
    ...Array.from(cells[5].querySelectorAll('[data-testid^="sublease-head-"]'), textWidth),
  );
  return [expiry, arrangement, ...Array.from(cells).slice(6, 10).map(textWidth)];
}

function measureGeometry(shell, header, body) {
  const available = body.parentElement.clientWidth;
  if (!available || !document.createRange().getBoundingClientRect) return null;

  const visible = Array.from({ length: 10 }, (_width, index) =>
    !(shell.clientWidth <= 900 && index >= 7)
    && !(shell.clientWidth <= 650 && (index === 5 || index === 6)));
  const lastVisible = visible.lastIndexOf(true);
  // Match the scoped edge-cell CSS, including the existing responsive readers.
  // Header insets are symmetric to retain label centres; body values keep their
  // original alignment and only the outer inset grows.
  const bodyPadding = index => {
    const normal = index >= 7 ? NUMERIC_PADDING : BODY_PADDING;
    return index === 0 || index === lastVisible ? normal / 2 + EDGE_INSET : normal;
  };
  const headers = Array.from(header.querySelectorAll("th")).slice(0, 10);
  const minima = headers.map((cell, index) => Math.max(
    index < 4 ? IDENTITY_MIN_WIDTHS[index] : 0,
    Math.ceil(headerContentWidth(cell, index === 0 || index === lastVisible)),
  ));
  for (const row of body.querySelectorAll('tr[data-testid^="leases-list-row-"]')) {
    // Measure glyphs rather than a previously allocated cell width: measuring
    // the cell itself would ratchet widths up on every resize. Pills include
    // their own internal padding, whereas the Office name can be ellipsized.
    for (let index = 0; index < 4; index++) {
      const cell = row.cells[index];
      const content = index === 0 ? cell.querySelector("[data-row-name]")
        : cell.querySelector('[data-testid="health-pill"], [data-testid="status-pill"]') ?? cell.firstElementChild;
      const width = index === 0 && content
        ? textWidth(content.firstElementChild)
          + (content.lastElementChild?.getBoundingClientRect().width ?? 0)
          + parseFloat(getComputedStyle(content).columnGap || "0")
        : content?.getBoundingClientRect().width ?? 0;
      minima[index] = Math.max(minima[index], Math.ceil(width) + bodyPadding(index));
    }
    trailingContentWidths(row).forEach((width, index) => {
      minima[index + 4] = Math.max(minima[index + 4], Math.ceil(width) + bodyPadding(index + 4));
    });
  }
  const minimum = minima.reduce((sum, width, index) => sum + (visible[index] ? width : 0), 0);
  const tableWidth = Math.max(available, minimum);
  const surplusPerColumn = (tableWidth - minimum) / visible.filter(Boolean).length;
  // Identical measured colgroups own both tables. Hidden tracks get nothing;
  // the retained empty legacy-compatible cell no longer owns spare width.
  const widths = [...minima.map((width, index) => visible[index] ? width + surplusPerColumn : 0), 0];
  return { widths, tableWidth };
}

// Only the active v2 boundary opts in. No lease values are changed or cached;
// native glyph measurements keep dates, supplied terms and rent fully visible.
export function useLeasesListGeometry({ enabled, shellRef, headerRef, bodyRef, rows, density }) {
  const [geometry, setGeometry] = useState(INITIAL_GEOMETRY);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const shell = shellRef.current;
    const header = headerRef.current?.querySelector("table");
    const body = bodyRef.current?.querySelector("table");
    if (!shell || !header || !body) return undefined;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      const next = measureGeometry(shell, header, body);
      if (!next) return;
      setGeometry((previous) => previous.tableWidth === next.tableWidth
        && previous.widths.every((width, index) => width === next.widths[index]) ? previous : next);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(shell);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [enabled, shellRef, headerRef, bodyRef, rows, density]);

  return geometry;
}
